import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useAuth } from "@/context/AuthContext";

/**
 * First-visit welcome overlay. Only shows once per user (localStorage flag).
 * Admins never see it.
 */
export default function WelcomeModal() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!user) return;
    if (user.role !== "student") return;
    const key = `boardroom.welcome.seen.${user.id}`;
    if (localStorage.getItem(key) === "1") return;
    // small delay so it doesn't feel jumpy on mount
    const t = setTimeout(() => setOpen(true), 350);
    return () => clearTimeout(t);
  }, [user]);

  const close = () => {
    if (user?.id) localStorage.setItem(`boardroom.welcome.seen.${user.id}`, "1");
    setOpen(false);
  };

  if (!open || !user) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] bg-black/95 backdrop-blur-md flex items-center justify-center p-4 md:p-8"
      style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0 }}
      data-testid="welcome-modal"
      onClick={(e) => { if (e.target === e.currentTarget) close(); }}
    >
      <div className="max-w-2xl w-full border border-[#D4AF37]/40 bg-gradient-to-br from-[#0d0d0d] via-[#0a0a0a] to-[#050505] relative overflow-hidden">
        {/* Gold corner ornaments */}
        <div className="absolute top-0 left-0 w-16 h-16 border-t border-l border-[#D4AF37]" />
        <div className="absolute top-0 right-0 w-16 h-16 border-t border-r border-[#D4AF37]" />
        <div className="absolute bottom-0 left-0 w-16 h-16 border-b border-l border-[#D4AF37]" />
        <div className="absolute bottom-0 right-0 w-16 h-16 border-b border-r border-[#D4AF37]" />

        <div className="p-10 md:p-14 text-center">
          <div className="text-[#D4AF37] text-3xl mb-4 tracking-[0.4em]">◇ ◆ ◇</div>
          <p className="overline text-[#D4AF37] mb-3">Private Trading Syndicate</p>
          <h1 className="font-display text-4xl md:text-5xl leading-tight mb-3">
            Welcome to <span className="text-[#D4AF37]">The Boardroom</span>
            <span className="text-[#D4AF37]">.</span>
          </h1>
          <p className="text-neutral-400 max-w-lg mx-auto text-[15px] leading-relaxed font-body mb-2">
            <span className="text-white">{user.name.split(" ")[0]}</span> — you now hold a seat at the table.
          </p>
          <div className="gold-divider my-8 mx-auto max-w-xs" />
          <p className="text-neutral-300 max-w-md mx-auto text-[14px] leading-[1.9] font-body mb-2">
            Inside these doors you'll move from foundation to institutional-grade execution across
            <span className="text-[#D4AF37]"> Forex</span>,
            <span className="text-[#D4AF37]"> Futures</span> &
            <span className="text-[#D4AF37]"> Commodities</span> — no fluff, no filler.
          </p>
          <p className="text-neutral-500 max-w-md mx-auto text-[13px] leading-relaxed font-body italic mb-10">
            "Read the delivery. Front-run the crowd. Protect the capital."
          </p>
          <button
            onClick={close}
            data-testid="welcome-enter"
            className="btn-gold px-10 py-3 text-xs uppercase tracking-[0.3em]"
          >
            Enter the Boardroom
          </button>
          <p className="text-[10px] uppercase tracking-widest text-neutral-600 mt-8">
            ◇ Level 1 unlocked · 7 more await
          </p>
        </div>
      </div>
    </div>,
    document.body
  );
}
